import { z } from 'zod';
import { LicenseError } from '../../../../src/lib/errors';
import { LOG_CLIENT_NAME } from '../../../../src/lib/instrumentation/consts';
import { logError } from '../../../actions/logger/logServices';
import { BusinessInformation } from '../[licenseType]/(steps)/business-information/forms/types/businessInformation';
import { CustomerInformation } from '../[licenseType]/(steps)/customer-information/customerInformation.schema';
import { PaymentInformation } from '../[licenseType]/(steps)/summary-payment/summaryPayment.schema';
import { LicenseFlowTypeValue } from '../[licenseType]/flows';
import { useLicenseStore } from '../[licenseType]/licenseStore';
import { LicensePlan } from '../[licenseType]/types/licenses';

export interface SubmitPurchaseInquiryProps {
  kodaMailbox: string;
  licenseSlug: string;
  flowType?: LicenseFlowTypeValue | null;
}

type PurchaseInquiryPayload = {
  customerInformation: CustomerInformation;
  businessInformation?: BusinessInformation;
  licensePlan?: LicensePlan;
  paymentInformation?: PaymentInformation;
  mailboxOverride?: string;
  flowType?: LicenseFlowTypeValue | null;
};

const purchaseInquiryResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

export async function submitPurchaseInquiry(props: SubmitPurchaseInquiryProps) {
  const { licenseSlug, kodaMailbox, flowType } = props;

  const licenseStore = useLicenseStore.getState();
  const businessInformation = licenseStore.businessInformation;

  const payload: PurchaseInquiryPayload = {
    customerInformation: licenseStore.customerInformation,
    businessInformation: businessInformation,
    licensePlan: licenseStore.licensePlan,
    paymentInformation: licenseStore.paymentInformation,
    mailboxOverride: kodaMailbox || undefined,
    flowType: flowType ?? undefined,
  };

  const res = await fetch(`/licenses/${licenseSlug}/purchase-inquiry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const json = await (async () => {
    try {
      return await res.json();
    } catch (e) {
      return new LicenseError(
        `JSON parse failed | licenseCode=${payload.businessInformation?.licenseCode} | ${String(e)}`,
      );
    }
  })();

  if (json instanceof LicenseError) {
    logError(json, {
      instance: LOG_CLIENT_NAME,
      processName: 'licenses',
      action: 'purchase-inquiry',
      step: 'json-parse',
    });
    return { ok: false, error: 'Invalid JSON in purchase inquiry response' };
  }

  const parsed = purchaseInquiryResponseSchema.safeParse(json);

  if (!parsed.success) {
    logError(parsed.error, {
      instance: LOG_CLIENT_NAME,
      processName: 'licenses',
      action: 'purchase-inquiry',
      step: 'schema-validation',
    });
    return { ok: false, error: 'Purchase inquiry failed schema validation' };
  }

  return { ok: res.ok && parsed.data.success === true, error: parsed.data.error };
}
